import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Check, X as XIcon } from 'lucide-react';
import { ThemedText } from '../index';
import { MiniMap } from '../index';
import { renderFieldTypeIcon } from '../../utils/iconUtils';
import { WaveformAudioPlayer } from './WaveformAudioPlayer';
import { useTimezone } from '../../context/TimezoneContext';
import { evaluateRules } from '../../utils/ruleEvaluator';

/**
 * ReadOnlyFormRenderer - Displays form submissions with disabled form controls
 *
 * This component renders form fields as actual form controls (inputs, checkboxes, selects, etc.)
 * in a disabled/read-only state, providing a more visual and compact representation than
 * simple label/value pairs.
 *
 * @param {Object} props
 * @param {Array} props.schema - Array of field definitions from convertFormSchemaDefinitionToFields()
 * @param {Object} props.formData - Submission data object (key-value pairs)
 * @param {Array} props.attachments - Array of attachments for image fields
 * @param {boolean} props.hideEmptyFields - Whether to hide fields with no data
 * @param {Array} props.childSubmissions - Array of child submissions for repeated forms
 * @param {Object} props.theme - Theme context
 * @param {Function} props.onImageClick - Callback when image is clicked
 * @param {Function} props.onLocationClick - Callback when location is clicked
 */

// ==================== Styled Components ====================

const ReadOnlyFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const ReadOnlyFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  padding: ${props => props.theme.spacing.sm} 0;
`;

const ReadOnlyLabel = styled(ThemedText)`
  color: ${props => props.theme.colors.muted};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${props => props.theme.colors.muted};
`;

const DisabledInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  font-family: ${props => props.theme.typography.fontFamily};
  opacity: 0.9;
  cursor: default;

  &:disabled {
    cursor: default;
  }

  &::placeholder {
    color: ${props => props.theme.colors.muted};
  }
`;

const DisabledTextArea = styled.textarea`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  font-family: ${props => props.theme.typography.fontFamily};
  opacity: 0.9;
  cursor: default;
  resize: vertical;
  min-height: 96px;
  box-sizing: border-box;

  &:disabled {
    cursor: default;
  }

  &::placeholder {
    color: ${props => props.theme.colors.muted};
  }
`;

const DisabledSelect = styled.select`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  font-family: ${props => props.theme.typography.fontFamily};
  opacity: 0.9;
  cursor: default;

  &:disabled {
    cursor: default;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
  }
`;

const CheckboxRadioContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
`;

const CheckboxRadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  cursor: default;
  user-select: none;
`;

const DisabledCheckbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${props => props.theme.colors.tint};
  cursor: default;
  opacity: 0.9;
  flex-shrink: 0;

  &:disabled {
    cursor: default;
  }
`;

const OptionLabel = styled.span`
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
`;

// Layout field styled components
const SubmissionHeader = styled.div`
  margin: ${props => props.theme.spacing.lg} 0 ${props => props.theme.spacing.md} 0;
  font-weight: 600;
  font-size: ${props => {
    const level = props.level || 'h3';
    const sizes = {
      h1: props.theme.typography.sizes.xxlarge,
      h2: props.theme.typography.sizes.xlarge,
      h3: props.theme.typography.sizes.large,
      h4: props.theme.typography.sizes.default,
      h5: props.theme.typography.sizes.default,
      h6: props.theme.typography.sizes.small,
    };
    return sizes[level] || props.theme.typography.sizes.default;
  }};
  color: ${props => props.theme.colors.text};
  text-align: ${props => props.align || 'left'};
  border-bottom: ${props => props.level === 'h1' || props.level === 'h2'
    ? `2px solid ${props.theme.colors.border}`
    : 'none'};
  padding-bottom: ${props => props.level === 'h1' || props.level === 'h2'
    ? props.theme.spacing.sm
    : '0'};
`;

const SubmissionDivider = styled.hr`
  border: none;
  height: 1px;
  background-color: ${props => props.theme.colors.border};
  margin: ${props => props.theme.spacing.lg} 0;
`;

// Image field styled components
const ImageGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.sm};
`;

const ImageThumbnailContainer = styled.div`
  position: relative;
  padding-top: 100%;
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
  border: 1px solid ${props => props.theme.colors.border};
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`;

const ImageThumbnail = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ImageFileName = styled(ThemedText)`
  margin-top: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
  text-align: center;
  word-break: break-word;
`;

// Geolocation styled components
const GeolocationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.sm};
`;

const CoordinatesText = styled(ThemedText)`
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
`;

// Empty value styled component
const EmptyValue = styled(ThemedText)`
  color: ${props => props.theme.colors.muted};
  font-style: italic;
  font-size: ${props => props.theme.typography.sizes.default};
`;

// Editable field styled components (used in edit mode)
const EditableInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.tint};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  font-family: ${props => props.theme.typography.fontFamily};
  box-sizing: border-box;

  &:focus {
    outline: 2px solid ${props => props.theme.colors.tint};
    outline-offset: -1px;
  }
`;

const EditableTextArea = styled.textarea`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.tint};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  font-family: ${props => props.theme.typography.fontFamily};
  resize: vertical;
  min-height: 80px;
  box-sizing: border-box;

  &:focus {
    outline: 2px solid ${props => props.theme.colors.tint};
    outline-offset: -1px;
  }
`;

const EditableSelect = styled.select`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.tint};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  font-family: ${props => props.theme.typography.fontFamily};
  cursor: pointer;

  &:focus {
    outline: 2px solid ${props => props.theme.colors.tint};
    outline-offset: -1px;
  }
`;

const EditableCheckbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${props => props.theme.colors.tint};
  cursor: pointer;
  flex-shrink: 0;
`;

const EditableOption = styled.label`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  cursor: pointer;
  user-select: none;
`;

const ReadOnlyHint = styled.span`
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
  font-style: italic;
  margin-top: ${props => props.theme.spacing.xs};
  display: block;
`;

// ==================== Helper Functions ====================

/**
 * Check if a field is a layout field (non-data field)
 */
const isLayoutField = (field) => {
  return ['header', 'divider', 'spacer'].includes(field.type);
};

/**
 * Check if a field value is empty
 */
const isFieldEmpty = (value) => {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return true;
  return false;
};

/**
 * Format date value for display
 */
const formatDateValue = (value, type) => {
  if (!value) return '';

  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;

    if (type === 'date') {
      return date.toLocaleDateString();
    } else if (type === 'time') {
      return date.toLocaleTimeString();
    } else {
      return date.toLocaleString();
    }
  } catch (e) {
    return value;
  }
};

// Tag chip styled components
const TagChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.xs};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const TagChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: ${props => props.color || props.theme.colors.tint};
  color: #fff;
  border-radius: 12px;
  padding: 3px 10px;
  font-size: ${props => props.theme.typography.sizes.small};
`;

const TagChipRemoveBtn = styled.button`
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 0;
  font-size: 14px;
  line-height: 1;
  opacity: 0.8;
  &:hover { opacity: 1; }
`;

const TagChipOutline = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  color: ${props => props.color || props.theme.colors.tint};
  border: 1.5px solid ${props => props.color || props.theme.colors.tint};
  border-radius: 12px;
  padding: 3px 10px;
  font-size: ${props => props.theme.typography.sizes.small};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.5 : 1};
  &:hover {
    background: ${props => props.$disabled ? 'transparent' : (props.color || props.theme.colors.tint) + '18'};
  }
`;

const TagFieldContainerStyled = styled.div`
  border: 1px solid ${props => props.theme.colors.tint};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing.sm};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  min-height: 44px;
  &:focus-within { border-color: ${props => props.theme.colors.tint}; }
`;

const TagTextInputStyled = styled.input`
  border: none;
  outline: none;
  width: 100%;
  padding: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.typography.sizes.default};
  background: transparent;
  color: ${props => props.theme.colors.text};
  font-family: ${props => props.theme.typography.fontFamily};
  &::placeholder { color: ${props => props.theme.colors.muted}; }
`;

/**
 * Editable tags chip-picker — needs local state so it's a sub-component
 */
const EditableTagsField = ({ field, value, onFieldChange, theme }) => {
  const [inputText, setInputText] = React.useState('');

  const tags = Array.isArray(value)
    ? value
    : (value ? (() => { try { const p = JSON.parse(value); return Array.isArray(p) ? p : String(value).split(',').map(t => t.trim()).filter(Boolean); } catch { return String(value).split(',').map(t => t.trim()).filter(Boolean); } })() : []);

  const options = field.props?.options || [];
  const allowCustom = field.props?.allowCustom !== false;
  const maxTags = field.props?.maxTags || null;
  const atMax = maxTags !== null && tags.length >= maxTags;

  const update = (newTags) => onFieldChange && onFieldChange(field.id, newTags);

  return (
    <div>
      {/* Bordered container: selected chips + text input */}
      <TagFieldContainerStyled theme={theme}>
        {tags.length > 0 && (
          <TagChipsRow theme={theme}>
            {tags.map(tag => {
              const opt = options.find(o => o.value === tag);
              const color = opt?.color || theme.colors.tint;
              const label = opt?.label || tag;
              return (
                <TagChip key={tag} color={color} theme={theme}>
                  {label}
                  <TagChipRemoveBtn onClick={() => update(tags.filter(t => t !== tag))}><XIcon size={12} /></TagChipRemoveBtn>
                </TagChip>
              );
            })}
          </TagChipsRow>
        )}

        {allowCustom && (
          <TagTextInputStyled
            placeholder={atMax ? `Max ${maxTags} tags reached` : 'Type and press Enter...'}
            disabled={atMax}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && inputText.trim()) {
                e.preventDefault();
                const v = inputText.trim();
                if (!tags.includes(v) && !atMax) update([...tags, v]);
                setInputText('');
              }
            }}
            theme={theme}
          />
        )}
      </TagFieldContainerStyled>

      {/* Predefined options not yet selected (below container) */}
      {options.filter(opt => !tags.includes(opt.value)).length > 0 && (
        <TagChipsRow theme={theme} style={{ marginTop: theme.spacing.xs }}>
          {options.filter(opt => !tags.includes(opt.value)).map(opt => (
            <TagChipOutline
              key={opt.value}
              color={opt.color || theme.colors.tint}
              theme={theme}
              $disabled={atMax}
              onClick={() => !atMax && update([...tags, opt.value])}
            >
              + {opt.label}
            </TagChipOutline>
          ))}
        </TagChipsRow>
      )}

      {maxTags && (
        <div style={{ fontSize: theme.typography.sizes.small, color: theme.colors.muted, marginTop: theme.spacing.xs }}>
          {tags.length}/{maxTags} tags selected
        </div>
      )}
    </div>
  );
};

// ==================== DateTime Helpers ====================

function formatDateTimeDisplay(value, precision, tz) {
  if (!value) return '';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    const timeZone = tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (precision === 'date') {
      return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(d);
    }
    if (precision === 'time') {
      return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone }).format(d);
    }
    return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true, timeZone }).format(d);
  } catch {
    return value;
  }
}

function formatValueForHtmlInput(value, precision) {
  if (!value) return '';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    if (precision === 'date') return d.toISOString().split('T')[0];
    if (precision === 'time') {
      return d.toTimeString().slice(0, 5);
    }
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return value;
  }
}

// ==================== Main Component ====================

export const ReadOnlyFormRenderer = ({
  schema,
  formData = {},
  attachments = [],
  hideEmptyFields = false,
  childSubmissions = [],
  theme,
  onImageClick,
  onLocationClick,
  isEditable = false,
  onFieldChange = null,
}) => {
  const { effectiveTimezone } = useTimezone();

  // Advanced Field Rules — evaluate `format` styles once per submission so the
  // detail view can colour fields/values that match their rules.
  const ruleResult = useMemo(() => {
    try {
      const canonical = (Array.isArray(schema) ? schema : []).map(f => ({
        key: f.id,
        options: f.props?.options,
        rules: f.props?.rules,
      }));
      return evaluateRules(canonical, formData);
    } catch (e) {
      return { hiddenFieldKeys: [], optionFilters: {}, fieldStyles: {} };
    }
  }, [schema, formData]);
  const ruleFieldStyles = ruleResult.fieldStyles || {};
  // Fields hidden by a visibility rule given the submitted answers were never
  // shown to the submitter — omit them here too instead of rendering "Not provided".
  const ruleHiddenKeySet = useMemo(
    () => new Set(ruleResult.hiddenFieldKeys || []),
    [ruleResult]
  );

  // Filter fields based on hideEmptyFields setting
  const filteredFields = schema.filter(field => {
    // Always show layout fields (they provide structure)
    if (isLayoutField(field)) return true;

    // Conditionally hidden by an active visibility rule — omit entirely.
    if (ruleHiddenKeySet.has(field.id)) return false;

    // Hidden fields (e.g., auto-populated lab results) only render when they have a value —
    // an empty hidden field would be confusing noise in the detail view.
    if (field.props?.isHidden && isFieldEmpty(formData[field.id])) {
      return false;
    }

    // If hideEmptyFields is enabled, filter out empty data fields
    if (hideEmptyFields && isFieldEmpty(formData[field.id])) {
      return false;
    }

    return true;
  });

  /**
   * Render a single field based on its type
   */
  const renderField = (field) => {
    const fieldValue = formData[field.id];
    const fieldType = field.type;

    // Handle layout fields
    if (fieldType === 'header') {
      const headerText = field.props.text || field.props.label || '';
      if (!headerText) return null;

      return (
        <SubmissionHeader
          key={field.id}
          theme={theme}
          level={field.props.level || 'h3'}
          align={field.props.align || 'left'}
        >
          {headerText}
        </SubmissionHeader>
      );
    }

    if (fieldType === 'divider') {
      return <SubmissionDivider key={field.id} theme={theme} />;
    }

    if (fieldType === 'spacer') {
      return null; // Spacers don't render in read-only view
    }

    const isAutoPopulatedLab = field.props?.isHidden && field.props?.labResultSource;
    const labUnit = field.props?.labResultSource?.unitHint;

    // Advanced Field Rules — `format` style for this field (if any matched).
    const fieldStyle = ruleFieldStyles[field.id];
    const hasFieldStyle = !!fieldStyle && (fieldStyle.bg || fieldStyle.fg || fieldStyle.flag);

    // All data fields are wrapped in ReadOnlyFieldContainer
    return (
      <ReadOnlyFieldContainer
        key={field.id}
        theme={theme}
        style={isAutoPopulatedLab ? {
          backgroundColor: theme?.colors?.backgroundSecondary,
          borderLeft: `3px solid ${theme?.colors?.tint}`,
          paddingLeft: theme?.spacing?.md,
        } : undefined}
      >
        <LabelRow theme={theme}>
          {renderFieldTypeIcon(field.type, 13)}
          <ReadOnlyLabel theme={theme}>
            {field.props.label}
            {labUnit ? ` (${labUnit})` : ''}
            {field.props.required && ' *'}
          </ReadOnlyLabel>
          {hasFieldStyle && fieldStyle.flag ? (
            <span
              style={{
                marginLeft: theme?.spacing?.sm,
                fontSize: 12,
                fontWeight: 600,
                padding: `2px ${theme?.spacing?.sm}`,
                borderRadius: theme?.borderRadius?.lg,
                ...(fieldStyle.bg ? { backgroundColor: fieldStyle.bg } : {}),
                ...(fieldStyle.fg ? { color: fieldStyle.fg } : {}),
                border: '1px solid rgba(0,0,0,0.12)',
              }}
            >
              {fieldStyle.flag}
            </span>
          ) : null}
          {isAutoPopulatedLab ? (
            <span
              style={{
                marginLeft: theme?.spacing?.sm,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                padding: `2px ${theme?.spacing?.xs}`,
                borderRadius: theme?.borderRadius?.sm,
                backgroundColor: theme?.colors?.tintMuted || theme?.colors?.background,
                color: theme?.colors?.tint,
                border: `1px solid ${theme?.colors?.tint}`,
              }}
              title="Auto-populated from a linked lab report"
            >
              🧪 Lab
            </span>
          ) : null}
        </LabelRow>
        {hasFieldStyle && (fieldStyle.bg || fieldStyle.fg) ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: `2px ${theme?.spacing?.sm}`,
              borderRadius: theme?.borderRadius?.md,
              ...(fieldStyle.bg ? { backgroundColor: fieldStyle.bg } : {}),
              ...(fieldStyle.fg ? { color: fieldStyle.fg } : {}),
            }}
          >
            {renderFieldValue(field, fieldValue)}
          </span>
        ) : (
          renderFieldValue(field, fieldValue)
        )}
      </ReadOnlyFieldContainer>
    );
  };

  /**
   * Render the value for a data field
   */
  const renderFieldValue = (field, value) => {
    const fieldType = field.type;
    const isMedia = ['image_camera', 'drawing', 'voice', 'file'].includes(fieldType);
    const isGeo = fieldType === 'geolocation';

    // In edit mode, media and geo fields remain read-only
    const effectivelyReadOnly = !isEditable || isMedia || isGeo;

    // Handle empty values (read-only mode shows placeholder; edit mode shows empty input)
    if (isFieldEmpty(value) && effectivelyReadOnly) {
      if (isMedia) {
        const fieldAttachments = attachments.filter(att => att.fieldName === field.id);
        if (fieldAttachments.length === 0) {
          return <EmptyValue theme={theme}>No attachments</EmptyValue>;
        }
      } else if (!isGeo) {
        return <EmptyValue theme={theme}>Not provided</EmptyValue>;
      }
    }

    // Tags — render as pill badges
    if (fieldType === 'tags') {
      const tags = Array.isArray(value)
        ? value
        : (value ? (() => { try { const p = JSON.parse(value); return Array.isArray(p) ? p : String(value).split(',').map(t => t.trim()).filter(Boolean); } catch { return String(value).split(',').map(t => t.trim()).filter(Boolean); } })() : []);
      const options = field.props?.options || [];

      if (isEditable) {
        return (
          <EditableTagsField
            field={field}
            value={value}
            onFieldChange={onFieldChange}
            theme={theme}
          />
        );
      }

      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {tags.length === 0
            ? <span style={{ color: theme.colors.muted, fontSize: '13px' }}>—</span>
            : tags.map(tag => {
                const opt = options.find(o => o.value === tag);
                const color = opt?.color || theme.colors.tint;
                const label = opt?.label || tag;
                return (
                  <span key={tag} style={{
                    background: color,
                    color: '#fff',
                    borderRadius: '12px',
                    padding: '2px 10px',
                    fontSize: '13px'
                  }}>{label}</span>
                );
              })
          }
        </div>
      );
    }

    // Text input types
    if (['input', 'email', 'phone', 'url'].includes(fieldType)) {
      if (isEditable) {
        return (
          <EditableInput
            type="text"
            value={value || ''}
            theme={theme}
            onChange={(e) => onFieldChange && onFieldChange(field.id, e.target.value)}
          />
        );
      }
      return (
        <DisabledInput
          type="text"
          value={value || ''}
          disabled
          theme={theme}
        />
      );
    }

    // Number input
    if (['number', 'currency', 'percentage'].includes(fieldType)) {
      if (isEditable) {
        return (
          <EditableInput
            type="number"
            value={value ?? ''}
            theme={theme}
            onChange={(e) => onFieldChange && onFieldChange(field.id, e.target.value === '' ? '' : Number(e.target.value))}
          />
        );
      }
      let displayValue = value;
      if (fieldType === 'currency' && typeof value === 'number') {
        displayValue = `$${value.toFixed(2)}`;
      } else if (fieldType === 'percentage' && typeof value === 'number') {
        displayValue = `${value}%`;
      }
      return (
        <DisabledInput
          type="text"
          value={displayValue || ''}
          disabled
          theme={theme}
        />
      );
    }

    // Date and time inputs
    if (fieldType === 'date') {
      if (isEditable) {
        return (
          <EditableInput
            type="date"
            value={value || ''}
            theme={theme}
            onChange={(e) => onFieldChange && onFieldChange(field.id, e.target.value)}
          />
        );
      }
      return (
        <DisabledInput
          type="date"
          value={value || ''}
          disabled
          theme={theme}
        />
      );
    }

    if (fieldType === 'time') {
      if (isEditable) {
        return (
          <EditableInput
            type="time"
            value={value || ''}
            theme={theme}
            onChange={(e) => onFieldChange && onFieldChange(field.id, e.target.value)}
          />
        );
      }
      return (
        <DisabledInput
          type="time"
          value={value || ''}
          disabled
          theme={theme}
        />
      );
    }

    if (fieldType === 'datetime') {
      const precision = field.props?.precision || 'datetime';
      const htmlType = precision === 'date' ? 'date'
                     : precision === 'time' ? 'time'
                     : 'datetime-local';
      if (isEditable) {
        const editValue = formatValueForHtmlInput(value, precision);
        return (
          <EditableInput
            type={htmlType}
            value={editValue}
            theme={theme}
            onChange={(e) => onFieldChange && onFieldChange(field.id, e.target.value)}
          />
        );
      }
      const displayValue = formatDateTimeDisplay(value, precision, effectiveTimezone);
      return (
        <DisabledInput
          value={displayValue}
          disabled
          theme={theme}
        />
      );
    }

    // Textarea
    if (fieldType === 'textarea') {
      if (isEditable) {
        return (
          <EditableTextArea
            value={value || ''}
            rows={field.props.rows || 4}
            theme={theme}
            onChange={(e) => onFieldChange && onFieldChange(field.id, e.target.value)}
          />
        );
      }
      return (
        <DisabledTextArea
          value={value || ''}
          rows={field.props.rows || 4}
          disabled
          theme={theme}
        />
      );
    }

    // Select dropdown
    if (fieldType === 'select') {
      const options = field.props.options || [];
      if (isEditable) {
        return (
          <EditableSelect
            value={value || ''}
            theme={theme}
            onChange={(e) => onFieldChange && onFieldChange(field.id, e.target.value)}
          >
            <option value="">Select an option</option>
            {options.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </EditableSelect>
        );
      }
      return (
        <DisabledSelect value={value || ''} disabled theme={theme}>
          <option value="">Select an option</option>
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </DisabledSelect>
      );
    }

    // Toggle (single checkbox)
    if (fieldType === 'toggle') {
      const isChecked = value === true || value === 'true';
      if (isEditable) {
        return (
          <EditableOption theme={theme}>
            <EditableCheckbox
              type="checkbox"
              checked={isChecked}
              theme={theme}
              onChange={(e) => onFieldChange && onFieldChange(field.id, e.target.checked)}
            />
            <OptionLabel theme={theme}>
              {isChecked ? 'Yes' : 'No'}
            </OptionLabel>
          </EditableOption>
        );
      }
      return (
        <CheckboxRadioOption theme={theme}>
          <DisabledCheckbox
            type="checkbox"
            checked={isChecked}
            disabled
            theme={theme}
          />
          <OptionLabel theme={theme}>
            {isChecked ? 'Yes' : 'No'}
          </OptionLabel>
        </CheckboxRadioOption>
      );
    }

    // Checkbox group (multiple selection)
    if (fieldType === 'checkbox_group') {
      const options = field.props.options || [];
      const selectedValues = Array.isArray(value) ? value : [];

      if (isEditable) {
        return (
          <CheckboxRadioContainer theme={theme}>
            {options.map(opt => {
              const isChecked = selectedValues.includes(opt.value);
              return (
                <EditableOption key={opt.value} theme={theme}>
                  <EditableCheckbox
                    type="checkbox"
                    checked={isChecked}
                    theme={theme}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...selectedValues, opt.value]
                        : selectedValues.filter(v => v !== opt.value);
                      onFieldChange && onFieldChange(field.id, next);
                    }}
                  />
                  <OptionLabel theme={theme}>{opt.label}</OptionLabel>
                </EditableOption>
              );
            })}
          </CheckboxRadioContainer>
        );
      }

      return (
        <CheckboxRadioContainer theme={theme}>
          {options.map(opt => {
            const isChecked = selectedValues.includes(opt.value);
            return (
              <CheckboxRadioOption key={opt.value} theme={theme}>
                <DisabledCheckbox
                  type="checkbox"
                  checked={isChecked}
                  disabled
                  theme={theme}
                />
                <OptionLabel theme={theme}>{opt.label}</OptionLabel>
              </CheckboxRadioOption>
            );
          })}
        </CheckboxRadioContainer>
      );
    }

    // Radio group
    if (fieldType === 'radio') {
      const options = field.props.options || [];

      if (isEditable) {
        return (
          <CheckboxRadioContainer theme={theme}>
            {options.map(opt => {
              const isChecked = value === opt.value;
              return (
                <EditableOption key={opt.value} theme={theme}>
                  <EditableCheckbox
                    type="radio"
                    checked={isChecked}
                    theme={theme}
                    name={field.id}
                    onChange={() => onFieldChange && onFieldChange(field.id, opt.value)}
                  />
                  <OptionLabel theme={theme}>{opt.label}</OptionLabel>
                </EditableOption>
              );
            })}
          </CheckboxRadioContainer>
        );
      }

      return (
        <CheckboxRadioContainer theme={theme}>
          {options.map(opt => {
            const isChecked = value === opt.value;
            return (
              <CheckboxRadioOption key={opt.value} theme={theme}>
                <DisabledCheckbox
                  type="radio"
                  checked={isChecked}
                  disabled
                  theme={theme}
                  name={field.id}
                />
                <OptionLabel theme={theme}>{opt.label}</OptionLabel>
              </CheckboxRadioOption>
            );
          })}
        </CheckboxRadioContainer>
      );
    }

    // Image/Camera and Drawing fields — always read-only (Phase 1)
    if (fieldType === 'image_camera' || fieldType === 'drawing') {
      const fieldAttachments = attachments.filter(
        att => att.fieldName === field.id
      );

      if (fieldAttachments.length === 0) {
        return (
          <>
            <EmptyValue theme={theme}>No images</EmptyValue>
            {isEditable && <ReadOnlyHint theme={theme}>(editing not available)</ReadOnlyHint>}
          </>
        );
      }

      return (
        <>
          <ImageGallery theme={theme}>
            {fieldAttachments.map((att, idx) => (
              <div key={idx}>
                <ImageThumbnailContainer
                  theme={theme}
                  onClick={() => onImageClick && onImageClick(att)}
                >
                  <ImageThumbnail
                    src={att.fileUrl}
                    alt={att.fileName}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="150" height="150"%3E%3Crect fill="%23ddd" width="150" height="150"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EImage unavailable%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </ImageThumbnailContainer>
                <ImageFileName theme={theme}>{att.fileName}</ImageFileName>
              </div>
            ))}
          </ImageGallery>
          {isEditable && <ReadOnlyHint theme={theme}>(editing not available)</ReadOnlyHint>}
        </>
      );
    }

    // Voice field — audio player + transcript block (if enabled)
    if (fieldType === 'voice') {
      const voiceAttachments = attachments.filter(att => att.fieldName === field.id);
      // Transcript text is stored in a sibling FormSubmissionField with key
      // "{voiceKey}__transcript" — the submissions endpoint hydrates this into formData.
      const transcriptValue = formData?.[`${field.id}__transcript`];

      if (voiceAttachments.length === 0 && !transcriptValue) {
        return (
          <>
            <EmptyValue theme={theme}>No recording</EmptyValue>
            {isEditable && <ReadOnlyHint theme={theme}>(editing not available)</ReadOnlyHint>}
          </>
        );
      }

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
          {voiceAttachments.map((att, idx) => (
            <WaveformAudioPlayer
              key={att.id ?? idx}
              src={att.fileUrl}
              theme={theme}
              attachment={att}
            />
          ))}
          {voiceAttachments.length === 0 && transcriptValue && (
            <div style={{ color: theme.colors.text, fontSize: '13px', whiteSpace: 'pre-wrap' }}>
              {transcriptValue}
            </div>
          )}
          {isEditable && <ReadOnlyHint theme={theme}>(editing not available)</ReadOnlyHint>}
        </div>
      );
    }

    // File field — always read-only (Phase 1)
    if (fieldType === 'file') {
      return (
        <>
          <DisabledInput
            type="text"
            value={typeof value === 'object' ? JSON.stringify(value) : String(value || '')}
            disabled
            theme={theme}
          />
          {isEditable && <ReadOnlyHint theme={theme}>(editing not available)</ReadOnlyHint>}
        </>
      );
    }

    // Geolocation field — always read-only
    if (fieldType === 'geolocation') {
      if (!value || !value.latitude || !value.longitude) {
        return (
          <>
            <EmptyValue theme={theme}>No location data</EmptyValue>
            {isEditable && <ReadOnlyHint theme={theme}>(editing not available)</ReadOnlyHint>}
          </>
        );
      }

      return (
        <>
          <GeolocationContainer theme={theme}>
            <CoordinatesText theme={theme}>
              Coordinates: {value.latitude.toFixed(6)}, {value.longitude.toFixed(6)}
              {value.accuracy && ` (±${value.accuracy}m)`}
            </CoordinatesText>
            <MiniMap
              latitude={value.latitude}
              longitude={value.longitude}
              theme={theme}
            />
          </GeolocationContainer>
          {isEditable && <ReadOnlyHint theme={theme}>(editing not available)</ReadOnlyHint>}
        </>
      );
    }

    // For other field types, display as text
    if (isEditable) {
      return (
        <EditableInput
          type="text"
          value={typeof value === 'object' ? JSON.stringify(value) : String(value || '')}
          theme={theme}
          onChange={(e) => onFieldChange && onFieldChange(field.id, e.target.value)}
        />
      );
    }
    return (
      <DisabledInput
        type="text"
        value={typeof value === 'object' ? JSON.stringify(value) : String(value)}
        disabled
        theme={theme}
      />
    );
  };

  // Render empty state if no fields match the filter
  if (filteredFields.length === 0) {
    return (
      <ThemedText style={{ color: theme.colors.muted }}>
        {hideEmptyFields
          ? 'No filled fields to display'
          : 'No form fields available'}
      </ThemedText>
    );
  }

  // Render all filtered fields
  return (
    <ReadOnlyFormContainer theme={theme}>
      {filteredFields.map(field => renderField(field))}
    </ReadOnlyFormContainer>
  );
};

export default ReadOnlyFormRenderer;
